import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import StaffAllowancesHeader from 'components/hr/timeOffStaff/StaffAllowancesHeader';
import StaffTimeOffAllowance from 'components/hr/timeOffStaff/StaffTimeOffAllowance.component';
import DELAY from 'constants/delay.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonCalendarMode, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import { selectTimeOffCategories, selectTimeOffReportDto } from 'selectors/timeoff.selectors';
import asArray from 'utils/arrays/asArray.util';
import delayed from 'utils/functions/delayed.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import asObject from 'utils/objects/asObject.util';
import constant from 'utils/values/constant.util';

import CN from './StaffTimeOffAllowances.module.scss';

const ALERT_CLASSES = 'alert centered animated fadeInDown';

const INITIAL_ALERT = constant({
  message: '',
  type: '',
});

const StaffTimeOffAllowances = ({ report: reportFromProps, categories: categoriesFromProps, stylistList, calendarMode }) => {
  const { t } = useTranslation();
  const categories = asArray(categoriesFromProps);
  const report = asObject(reportFromProps);
  const stylists = asArray(stylistList);
  const { val: isAlertShown, yay: showAlert, nay: hideAlert } = useTip(false);
  const [alertInfo, setAlertInfo] = useState(INITIAL_ALERT);

  const handleAlert = useCallback(
    ({ type, message }) => {
      showAlert();
      setAlertInfo({
        type,
        message,
      });

      delayed(DELAY.autohide2, () => {
        hideAlert();
        setAlertInfo(INITIAL_ALERT);
      });
    },
    [hideAlert, showAlert],
  );

  return (
    <div data-bem="StaffTimeOffAllowances">
      <StaffAllowancesHeader />
      <div className={cn(CN.scroll, CN[calendarMode])}>
        <div data-bem="StaffTimeOffAllowances__categoryNamesHeader" className={CN.categoryNamesHeader}>
          <div className={CN.staffLabel}>{t('humanResources.timeOffSettings.allowancesPerStaff.staffMember')}</div>
          {categories.filter(alwaysSame).map((category) => (
            <div key={category.id} className={CN.catName}>
              <span className={CN.center}>{category.name}</span>
            </div>
          ))}
        </div>

        {stylists
          .filter((sty) => sty.user_name && sty.status !== 'deleted' && !sty.serves_as_waitlist && sty.has_column)
          .sort((a, b) => (a.user_name < b.user_name ? -1 : 1))
          .map((stylist) => {
            const s = asObject(stylist);
            const allowance = asObject(report[s.id]);

            return <StaffTimeOffAllowance key={s.id} stylist={s} allowance={allowance} index={s.id} onAlert={handleAlert} />;
          })}
      </div>

      {isAlertShown && <div className={cn(ALERT_CLASSES, alertInfo.type)}>{alertInfo.message}</div>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  stylistList: selectCurrentSalonStylists(state),
  categories: selectTimeOffCategories(state),
  report: selectTimeOffReportDto(state),
  calendarMode: selectCurrentSalonCalendarMode(state),
});

export default connect(mapStateToProps)(StaffTimeOffAllowances);
